import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, Instagram, Phone, Mail } from "lucide-react";
import { useCart } from "@/context/CartContext";

const navLinks = [
  { href: "/", label: "Ana Sayfa" },
  { href: "/hakkimizda", label: "Hakkımızda" },
  { href: "/urunler", label: "Ürünler" },
  { href: "/toptan-satis", label: "Toptan Satış" },
  { href: "/blog", label: "Blog" },
  { href: "/iletisim", label: "İletişim" },
];

export default function Layout({ children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { getCartCount } = useCart();
  const cartCount = getCartCount();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const isHomePage = location.pathname === "/";

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          isScrolled || !isHomePage
            ? "bg-[#F9F7F2] shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="section-container">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 md:gap-3"
              data-testid="logo-link"
            >
              <div className={`p-1.5 md:p-2 rounded transition-all duration-300 ${
                isScrolled || !isHomePage ? "bg-[#2C1810]" : "bg-[#2C1810]/90"
              }`}>
                <img
                  src="https://customer-assets.emergentagent.com/job_sumbul-kahve/artifacts/h88nhq4u_EC444525-41CF-4143-8369-B145948E246A.png"
                  alt="SümbülAğa Türk Kahvesi"
                  className="h-8 md:h-12 w-auto"
                />
              </div>
              <div className={`flex flex-col transition-colors duration-300 ${
                isScrolled || !isHomePage ? "text-[#0D3B2E]" : "text-[#F9F7F2]"
              }`}>
                <span className="font-display text-base md:text-2xl font-medium leading-tight">
                  SümbülAğa
                </span>
                <span className="text-[8px] md:text-xs tracking-[0.15em] md:tracking-[0.2em] uppercase opacity-70">
                  Türk Kahvesi
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`caption-text transition-colors duration-300 ${
                    location.pathname === link.href
                      ? "text-[#C5A059]"
                      : isScrolled || !isHomePage
                      ? "text-[#0D3B2E] hover:text-[#C5A059]"
                      : "text-[#F9F7F2] hover:text-[#C5A059]"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Cart & Menu */}
            <div className="flex items-center gap-4">
              <Link
                to="/sepet"
                className={`relative p-2 transition-colors duration-300 ${
                  isScrolled || !isHomePage
                    ? "text-[#0D3B2E] hover:text-[#C5A059]"
                    : "text-[#F9F7F2] hover:text-[#C5A059]"
                }`}
                data-testid="cart-button"
              >
                <ShoppingBag className="w-6 h-6" strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C5A059] text-[#0D3B2E] text-xs font-bold flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>

              <button
                onClick={() => setIsMenuOpen(true)}
                className={`lg:hidden p-2 transition-colors duration-300 ${
                  isScrolled || !isHomePage
                    ? "text-[#0D3B2E]"
                    : "text-[#F9F7F2]"
                }`}
                data-testid="mobile-menu-button"
              >
                <Menu className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="mobile-menu-overlay"
            data-testid="mobile-menu-overlay"
          >
            <button
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-6 right-6 text-[#F9F7F2] p-2"
              data-testid="close-mobile-menu"
            >
              <X className="w-8 h-8" strokeWidth={1.5} />
            </button>

            <nav className="flex flex-col items-center">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    className={`mobile-menu-link ${
                      location.pathname === link.href ? "text-[#C5A059]" : ""
                    }`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: navLinks.length * 0.1 }}
              >
                <Link
                  to="/sepet"
                  className="mobile-menu-link flex items-center gap-3"
                  data-testid="mobile-nav-sepet"
                >
                  <ShoppingBag className="w-8 h-8" strokeWidth={1.5} />
                  Sepet {cartCount > 0 && `(${cartCount})`}
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* WhatsApp Float Button */}
      <a
        href="https://wa.me/905493517121"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        data-testid="whatsapp-button"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

      {/* Footer */}
      <footer className="bg-[#0D3B2E] text-[#F9F7F2]">
        <div className="section-container py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_sumbul-kahve/artifacts/h88nhq4u_EC444525-41CF-4143-8369-B145948E246A.png"
                alt="SümbülAğa Türk Kahvesi"
                className="h-16 w-auto mb-4"
              />
              <p className="body-text text-[#F9F7F2]/70 mb-6">
                Geleneksel Türk kahvesi kültürünü modern premium sunumla
                birleştiriyoruz.
              </p>
              <div className="flex gap-4">
                <a
                  href="https://instagram.com/sumbulaga"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#F9F7F2]/70 hover:text-[#C5A059] transition-colors"
                  data-testid="footer-instagram"
                >
                  <Instagram className="w-5 h-5" strokeWidth={1.5} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="caption-text text-[#C5A059] mb-6">Hızlı Erişim</h4>
              <ul className="space-y-3">
                {navLinks.slice(1).map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="body-text text-[#F9F7F2]/70 hover:text-[#C5A059] transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="caption-text text-[#C5A059] mb-6">İletişim</h4>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-[#F9F7F2]/70">
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                  <span>+90 549 351 71 21</span>
                </li>
                <li className="flex items-center gap-3 text-[#F9F7F2]/70">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                  <span>info@sumbulaga.com</span>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="caption-text text-[#C5A059] mb-6">Bülten</h4>
              <p className="body-text text-[#F9F7F2]/70 mb-4 text-sm">
                Yeni ürünler ve kampanyalardan haberdar olun.
              </p>
              <form className="flex">
                <input
                  type="email"
                  placeholder="E-posta adresiniz"
                  className="flex-1 bg-transparent border-b border-[#F9F7F2]/30 py-2 text-sm focus:border-[#C5A059] outline-none transition-colors"
                  data-testid="newsletter-email"
                />
                <button
                  type="submit"
                  className="ml-4 text-[#C5A059] caption-text hover:text-[#F9F7F2] transition-colors"
                  data-testid="newsletter-submit"
                >
                  Gönder
                </button>
              </form>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 border-t border-[#F9F7F2]/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#F9F7F2]/50 text-sm">
              © 2024 SümbülAğa Türk Kahvesi. Tüm hakları saklıdır.
            </p>
            <div className="flex gap-6 text-[#F9F7F2]/50 text-sm">
              <a href="#" className="hover:text-[#C5A059] transition-colors">
                Gizlilik Politikası
              </a>
              <a href="#" className="hover:text-[#C5A059] transition-colors">
                Kullanım Koşulları
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
